import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/observable/throw';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/finally';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/timeout';

import { UserService } from './user.service';
import {
  NETWORK_ALLOWED,
  NETWORK_DENIED,
  NETWORK_UNKNOWN,
  WORK_FINISHED,
  WORK_NOT_STARTED,
  WORK_STARTED,
  WorkTimeRequestError,
  WorkTimeStatus
} from '../models/work-time.model';
import { HttpStatus } from '../shared.constants/all-constants';

const LOGIN_HEARTBEAT_INTERVAL = 60000;
const STATUS_REFRESH_INTERVAL = 3600000;
const REQUEST_TIMEOUT = 15000;
const LOST_RESPONSE_STATUSES = [
  HttpStatus.NETWORK_ERROR,
  HttpStatus.BAD_GATEWAY,
  HttpStatus.SERVICE_UNAVAILABLE,
  HttpStatus.GATEWAY_TIMEOUT
];
const INDEX_NOT_FOUND = -1;
const TIME_CAPTURE = 1;
const EMPTY_TIME = '-:-';
const TIME_PATTERN = /[T ](\d{2}:\d{2})/;
const TIMEOUT_ERROR = 'TimeoutError';
const ONLINE_EVENT = 'online';
const OFFLINE_EVENT = 'offline';
const FOCUS_EVENT = 'focus';
const PAGE_SHOW_EVENT = 'pageshow';
const VISIBILITY_EVENT = 'visibilitychange';
const RESULT_NOT_STARTED = 'WORK_LOG_NOT_STARTED';
const RESULT_CONFLICT = 'WORK_LOG_CONFLICT';
const MESSAGE_CHECKING = 'WORK_TIME_CHECKING';
const MESSAGE_OFFLINE = 'WORK_TIME_OFFLINE';
const MESSAGE_SESSION_EXPIRED = 'WORK_TIME_SESSION_EXPIRED';
const MESSAGE_NOT_STARTED = 'WORK_TIME_NOT_STARTED';
const MESSAGE_NETWORK_DENIED = 'WORK_TIME_NETWORK_DENIED';
const MESSAGE_NETWORK_UNKNOWN = 'WORK_TIME_NETWORK_UNKNOWN';
const MESSAGE_FINISH_CONFIRMED = 'WORK_TIME_FINISH_CONFIRMED';
const MESSAGE_LOGIN_CONFIRMED = 'WORK_TIME_LOGIN_CONFIRMED';
const MESSAGE_WAITING = 'WORK_TIME_WAITING_FOR_LOGIN';
const MESSAGE_CONFLICT = 'WORK_TIME_CONFLICT';
const MESSAGE_UNAVAILABLE = 'WORK_TIME_SERVICE_UNAVAILABLE';

@Injectable()
export class WorkTimeService implements OnDestroy {
  status: WorkTimeStatus;
  networkState = NETWORK_UNKNOWN;
  workTimeMessageKey = MESSAGE_CHECKING;
  loginTime = EMPTY_TIME;
  logoutTime = EMPTY_TIME;
  requestInFlight = false;

  private active = false;
  private heartbeatSubscription: Subscription;
  private statusRefreshSubscription: Subscription;
  private lifecycleSubscriptions = new Subscription();
  private sessionStop = new Subject<void>();
  private pendingStatusRead: { confirmLogin: boolean };

  constructor(
    private userService: UserService,
    @Inject(DOCUMENT) private pageDocument: Document
  ) {}

  get networkAllowed(): boolean {
    return !this.requestInFlight && this.networkState === NETWORK_ALLOWED;
  }

  get networkDenied(): boolean {
    return !this.requestInFlight && this.networkState === NETWORK_DENIED;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  startOrRefresh(): void {
    if (this.active) {
      this.refresh();
      return;
    }
    this.active = true;
    this.resetState();
    this.bindLifecycleEvents();
    this.startStatusRefresh();
    this.refresh();
  }

  stop(): void {
    this.active = false;
    this.sessionStop.next();
    this.stopHeartbeat();
    this.stopStatusRefresh();
    this.lifecycleSubscriptions.unsubscribe();
    this.lifecycleSubscriptions = new Subscription();
    this.resetState();
  }

  refresh(): void {
    this.readStatus(true);
  }

  finishWorkDay(): Observable<WorkTimeStatus> {
    if (!this.canFinishWorkDay()) {
      return Observable.throw({ status: HttpStatus.CONFLICT });
    }
    const previousStatus = this.status;
    this.requestInFlight = true;
    return this.timed(this.userService.writeLogoutTime())
      .catch(error => this.recoverFinish(error, previousStatus))
      .do(status => this.applyStatus(status), error => this.applyError(error))
      .finally(() => this.requestInFlight = false);
  }

  canFinishWorkDay(): boolean {
    return !this.requestInFlight
      && this.networkAllowed
      && !!this.status
      && (this.status.workState === WORK_STARTED
        || this.status.workState === WORK_FINISHED);
  }

  private readStatus(confirmLogin: boolean): void {
    if (this.pendingStatusRead && confirmLogin && !this.pageDocument.hidden) {
      this.pendingStatusRead.confirmLogin = true;
    }
    if (!this.active || this.requestInFlight) {
      return;
    }
    const pending = { confirmLogin: confirmLogin && !this.pageDocument.hidden };
    this.pendingStatusRead = pending;
    this.requestInFlight = true;
    this.workTimeMessageKey = MESSAGE_CHECKING;
    this.subscribeToStatus(pending);
  }

  private subscribeToStatus(pending: { confirmLogin: boolean }): void {
    this.statusRequest().subscribe(
      status => {
        this.completeStatusRead(pending);
        this.handleStatus(status, pending.confirmLogin);
      },
      error => {
        this.completeStatusRead(pending);
        this.handleStatusError(error);
      },
      () => this.completeStatusRead(pending)
    );
  }

  private completeStatusRead(pending: { confirmLogin: boolean }): void {
    if (this.pendingStatusRead !== pending) {
      return;
    }
    this.pendingStatusRead = undefined;
    this.requestInFlight = false;
  }

  private statusRequest(): Observable<WorkTimeStatus> {
    return this.timed(this.userService.getWorkTimeStatus());
  }

  private timed<T>(request: Observable<T>): Observable<T> {
    return request.timeout(REQUEST_TIMEOUT).takeUntil(this.sessionStop);
  }

  private handleStatus(status: WorkTimeStatus, confirmLogin: boolean): void {
    this.requestInFlight = false;
    this.applyStatus(status);
    if (confirmLogin) {
      this.confirmLoginIfNeeded();
    }
  }

  private handleStatusError(error: WorkTimeRequestError): void {
    this.requestInFlight = false;
    this.applyError(error);
    this.syncHeartbeat();
  }

  private confirmLoginIfNeeded(): void {
    if (!this.shouldConfirmLogin()) {
      return;
    }
    this.requestInFlight = true;
    this.timed(this.userService.writeLoginTime()).subscribe(
      status => this.handleStatus(status, false),
      error => this.handleLoginError(error)
    );
  }

  private handleLoginError(error: WorkTimeRequestError): void {
    this.requestInFlight = false;
    if (this.isLostResponse(error)) {
      this.readStatus(false);
      return;
    }
    this.applyError(error);
    this.syncHeartbeat();
  }

  private shouldConfirmLogin(): boolean {
    return this.active
      && !this.pageDocument.hidden
      && !this.requestInFlight
      && this.networkAllowed
      && !!this.status
      && this.status.workState === WORK_NOT_STARTED;
  }

  private startHeartbeat(): void {
    if (!this.active || this.heartbeatSubscription) {
      return;
    }
    this.heartbeatSubscription = Observable.timer(
      LOGIN_HEARTBEAT_INTERVAL, LOGIN_HEARTBEAT_INTERVAL)
      .takeUntil(this.sessionStop)
      .subscribe(() => this.readStatus(false));
  }

  private stopHeartbeat(): void {
    if (!this.heartbeatSubscription) {
      return;
    }
    this.heartbeatSubscription.unsubscribe();
    this.heartbeatSubscription = undefined;
  }

  private syncHeartbeat(): void {
    if (!this.status || this.status.workState === WORK_NOT_STARTED) {
      this.startHeartbeat();
      return;
    }
    this.stopHeartbeat();
  }

  private startStatusRefresh(): void {
    this.statusRefreshSubscription = Observable.timer(
      STATUS_REFRESH_INTERVAL, STATUS_REFRESH_INTERVAL)
      .takeUntil(this.sessionStop)
      .subscribe(() => this.readStatus(false));
  }

  private stopStatusRefresh(): void {
    if (this.statusRefreshSubscription) {
      this.statusRefreshSubscription.unsubscribe();
      this.statusRefreshSubscription = undefined;
    }
  }

  private bindLifecycleEvents(): void {
    this.lifecycleSubscriptions.add(Observable.fromEvent(window, ONLINE_EVENT)
      .subscribe(() => this.readStatus(false)));
    this.lifecycleSubscriptions.add(Observable.fromEvent(window, OFFLINE_EVENT)
      .subscribe(() => this.setOfflineHint()));
    this.lifecycleSubscriptions.add(Observable.fromEvent(window, FOCUS_EVENT)
      .subscribe(() => this.refresh()));
    this.lifecycleSubscriptions.add(Observable.fromEvent(window, PAGE_SHOW_EVENT)
      .subscribe(() => this.refresh()));
    this.lifecycleSubscriptions.add(Observable.fromEvent(this.pageDocument, VISIBILITY_EVENT)
      .subscribe(() => this.refreshWhenVisible()));
  }

  private refreshWhenVisible(): void {
    if (this.pageDocument.hidden) {
      if (this.pendingStatusRead) {
        this.pendingStatusRead.confirmLogin = false;
      }
      return;
    }
    this.refresh();
  }

  private applyStatus(status: WorkTimeStatus): void {
    this.status = status;
    this.networkState = status.networkState;
    this.loginTime = this.extractTime(status.loginAt);
    this.logoutTime = this.extractTime(status.logoutAt);
    this.workTimeMessageKey = this.resolveMessageKey(status);
    this.syncHeartbeat();
  }

  private recoverFinish(error: WorkTimeRequestError, previousStatus: WorkTimeStatus): Observable<WorkTimeStatus> {
    if (!this.isLostResponse(error)) {
      return Observable.throw(error);
    }
    return this.statusRequest().map(status => {
      if (status.workState !== WORK_FINISHED
        || status.businessDay !== previousStatus.businessDay
        || !status.logoutAt
        || (previousStatus.logoutAt && status.logoutAt <= previousStatus.logoutAt)) {
        throw error;
      }
      return status;
    });
  }

  private isLostResponse(error: WorkTimeRequestError): boolean {
    if (error && error.error && error.error.resultCode) {
      return false;
    }
    return !!error && (error.name === TIMEOUT_ERROR
      || LOST_RESPONSE_STATUSES.indexOf(error.status) !== INDEX_NOT_FOUND);
  }

  private applyError(error: WorkTimeRequestError): void {
    if (error && error.status === HttpStatus.UNAUTHORIZED) {
      this.networkState = NETWORK_UNKNOWN;
      this.workTimeMessageKey = MESSAGE_SESSION_EXPIRED;
      return;
    }
    if (navigator.onLine === false || (error && error.status === HttpStatus.NETWORK_ERROR)) {
      this.setOfflineHint();
      return;
    }
    this.networkState = error && error.status === HttpStatus.FORBIDDEN
      ? NETWORK_DENIED : NETWORK_UNKNOWN;
    this.workTimeMessageKey = this.resolveErrorKey(error);
  }

  private resolveMessageKey(status: WorkTimeStatus): string {
    if (status.resultCode === RESULT_NOT_STARTED) {
      return MESSAGE_NOT_STARTED;
    }
    if (status.networkState === NETWORK_DENIED) {
      return MESSAGE_NETWORK_DENIED;
    }
    if (status.networkState === NETWORK_UNKNOWN) {
      return MESSAGE_NETWORK_UNKNOWN;
    }
    if (status.workState === WORK_FINISHED) {
      return MESSAGE_FINISH_CONFIRMED;
    }
    return status.workState === WORK_STARTED ? MESSAGE_LOGIN_CONFIRMED : MESSAGE_WAITING;
  }

  private resolveErrorKey(error: WorkTimeRequestError): string {
    const resultCode = error && error.error ? error.error.resultCode : undefined;
    if (resultCode === RESULT_NOT_STARTED) {
      return MESSAGE_NOT_STARTED;
    }
    if (resultCode === RESULT_CONFLICT) {
      return MESSAGE_CONFLICT;
    }
    return error && error.status === HttpStatus.FORBIDDEN
      ? MESSAGE_NETWORK_DENIED : MESSAGE_UNAVAILABLE;
  }

  private setOfflineHint(): void {
    this.networkState = NETWORK_UNKNOWN;
    this.workTimeMessageKey = MESSAGE_OFFLINE;
  }

  private resetState(): void {
    this.pendingStatusRead = undefined;
    this.status = undefined;
    this.networkState = NETWORK_UNKNOWN;
    this.workTimeMessageKey = MESSAGE_CHECKING;
    this.loginTime = EMPTY_TIME;
    this.logoutTime = EMPTY_TIME;
    this.requestInFlight = false;
  }

  private extractTime(value: string | null): string {
    const match = value ? TIME_PATTERN.exec(value) : undefined;
    return match ? match[TIME_CAPTURE] : EMPTY_TIME;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/work-time.service.ts